import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import "../styles/contextual-popover.css";

export function ContextualPopover({ anchorRef, children, label, onClose, width = 456, anchorIndicator = false, horizontalAlign = "end", horizontalGutter = 8, className = "", dismissOnOutside = true, presentation, title, focusFirst = false }: {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  label: string;
  onClose(): void;
  width?: number;
  anchorIndicator?: boolean;
  horizontalAlign?: "end" | "center";
  horizontalGutter?: number;
  className?: string;
  dismissOnOutside?: boolean;
  presentation?: "content";
  title?: ReactNode;
  focusFirst?: boolean;
}) {
  const popoverRef = useRef<HTMLElement>(null);
  const outsideEnabled = useRef(dismissOnOutside);
  outsideEnabled.current = dismissOnOutside;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [position, setPosition] = useState({ left: 8, top: 8, width });
  const [above, setAbove] = useState(false);
  const [pointer, setPointer] = useState(20);
  useEffect(()=>{if(focusFirst)popoverRef.current?.querySelector<HTMLElement>('input:not(:disabled),button:not(:disabled),a[href]')?.focus();},[focusFirst]);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const popoverWidth = Math.min(width, window.innerWidth - horizontalGutter * 2);
      const height = popoverRef.current?.offsetHeight ?? 0;
      const placeBelow = anchor.bottom + 8 + height <= window.innerHeight - 8;
      setAbove(!placeBelow);
      const preferredLeft = horizontalAlign === "center" ? (anchor.left + anchor.right - popoverWidth) / 2 : anchor.right - popoverWidth;
      const left = Math.max(horizontalGutter, Math.min(preferredLeft, window.innerWidth - popoverWidth - horizontalGutter));
      setPointer(Math.max(12, Math.min(popoverWidth - 12, (anchor.left + anchor.right) / 2 - left)));
      setPosition({
        left,
        top: placeBelow ? anchor.bottom + 8 : Math.max(8, anchor.top - height - 8),
        width: popoverWidth,
      });
    };
    place();
    const observer = new ResizeObserver(place);
    if (popoverRef.current) observer.observe(popoverRef.current);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { observer.disconnect(); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [anchorRef, width, horizontalAlign, horizontalGutter]);

  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (outsideEnabled.current && !popoverRef.current?.contains(event.target as Node) && !anchorRef.current?.contains(event.target as Node)) {
        const anchor=anchorRef.current;
        closeRef.current();
        // Keep focus on an intentional outside control; otherwise restore the
        // trigger after a focused popover is removed from the document.
        requestAnimationFrame(()=>{if(document.activeElement===document.body&&anchor?.isConnected)anchor.focus();});
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); closeRef.current(); anchorRef.current?.focus(); }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [anchorRef]);

  return createPortal(<section ref={popoverRef} className={`contextual-popover ${className}${presentation ? " contextual-popover--content" : ""}${anchorIndicator ? ` contextual-popover--anchored${above ? " contextual-popover--above" : ""}` : ""}`} role="dialog" aria-modal="false" aria-label={label} style={{...position, "--popover-anchor-x": `${pointer}px`} as CSSProperties}>{title?<h2 className="contextual-popover-title">{title}</h2>:null}{presentation?<div className="contextual-popover-content">{children}</div>:children}</section>, document.body);
}
